import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AxiosError } from 'axios';
import {
  RunTransition,
  RunTransitionWithDialog,
  WorkflowService,
} from '../../services/workflow/workflow.service';
import { PayeePopupComponent } from '../../payee-popup/payee-popup.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatCheckbox } from '@angular/material/checkbox';
import { PayeeFormFieldComponent } from '../../payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeDropdownComponent } from '../../payee-form-fields/payee-dropdown/payee-dropdown.component';
import { PayeeInputDirective } from '../../payee-input/payee-input.directive';
import { PayeeIconButtonComponent } from '../../payee-buttons/payee-icon-button/payee-icon-button.component';
import { PayeeIconComponent } from '../../payee-icon/payee-icon.component';
import { PayeeButtonComponent } from '../../payee-buttons/payee-button/payee-button.component';
import { CurrencyPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

type DebtorStatus = {
  name: string;
  tax_number: string | null;
  status: string;
};

type DialogData = {
  transition_label: string;
  pro_forma_invoice_numbers: string[];
  debtorStatuses: DebtorStatus[];
  fees: {
    amount: number;
    currency: string;
  }[];
};

@Component({
  selector: 'app-start-fees-paid-dialog',
  templateUrl: './start-fees-paid-dialog.component.html',
  styleUrls: ['./start-fees-paid-dialog.component.scss'],
  imports: [
    PayeePopupComponent,
    MatProgressSpinner,
    FormsModule,
    ReactiveFormsModule,
    MatCheckbox,
    PayeeFormFieldComponent,
    PayeeDropdownComponent,
    PayeeInputDirective,
    PayeeIconButtonComponent,
    PayeeIconComponent,
    PayeeButtonComponent,
    CurrencyPipe,
    TranslateModule,
  ],
})
export class StartFeesPaidDialogComponent implements OnInit {
  private fb = inject(FormBuilder);
  private workflowService = inject(WorkflowService);
  private snackbar = inject(MatSnackBar);
  private dialogRef =
    inject<MatDialogRef<StartFeesPaidDialogComponent>>(MatDialogRef);
  data = inject<RunTransitionWithDialog>(MAT_DIALOG_DATA);

  readonly form = this.fb.group({
    link_pro_forma: this.fb.control(true, { nonNullable: true }),
    pro_forma: this.fb.control('', { nonNullable: true }),
  });

  get link_pro_forma() {
    return this.form.controls.link_pro_forma;
  }
  get pro_forma() {
    return this.form.controls.pro_forma;
  }

  dialogData?: DialogData;
  readonly dataLoading = signal(false);
  loading = false;

  async ngOnInit(): Promise<void> {
    try {
      this.dataLoading.set(true);
      this.dialogData = await this.workflowService.getDialogData<DialogData>(
        this.data,
      );

      if (this.dialogData.pro_forma_invoice_numbers.length > 0) {
        this.pro_forma.patchValue(this.dialogData.pro_forma_invoice_numbers[0]);
      }
    } catch (error: AxiosError | unknown) {
      let message = 'Hiba történt a díjak lekérdezésekor!';
      if (error instanceof AxiosError && error.response?.data?.message) {
        message = error.response.data.message;
      }
      this.snackbar.open(message, 'OK', { duration: 5000 });
      this.dialogRef.close();
    } finally {
      this.dataLoading.set(false);
    }
  }

  clearProForma(): void {
    this.pro_forma.patchValue('');
  }

  onSubmit = async () => {
    if (this.form.invalid) {
      return;
    }

    const { link_pro_forma, pro_forma } = this.form.getRawValue();

    const params: Record<string, unknown> = {
      ...(this.data.extraParams ?? {}),
    };

    if (link_pro_forma && pro_forma) {
      params.pro_forma_invoice_number = pro_forma;
    }

    this.loading = true;
    try {
      const payload: RunTransition = {
        caseId: this.data.caseId,
        transitionId: this.data.transitionId,
        params,
      };
      await this.workflowService.runTransition(payload);
      this.dialogRef.close(payload);
    } catch (error) {
      this.workflowService.showTransitionError(error);
    } finally {
      this.loading = false;
    }
  };

  onCancel = () => {
    this.dialogRef.close();
  };
}
